import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { resolveRouterPath, router } from '../router';
import { write } from '../language/lang';
import { styles as sharedStyles } from '../styles/shared-styles';
import { IShopAccess } from '../api/model/login-access-token';

@customElement('shop-select-page')
export class ShopSelectPage extends WithUserProfileConsumer(LitElement) {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 560px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }

      .page-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 24px;
        gap: 12px;
      }

      .user-info {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
      }

      .user-name {
        font-size: 0.95rem;
        font-weight: 600;
        color: var(--app-text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-email {
        font-size: 0.8rem;
        color: var(--app-text-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .shops {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .shop-card {
        cursor: pointer;
        transition: transform 0.05s ease;
      }
      .shop-card:hover {
        transform: translateY(-1px);
      }
      .name-row {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .empty {
        text-align: center;
        padding: 32px 16px;
      }
    `,
  ];

  private _signOut() {
    window.dispatchEvent(new CustomEvent('logout-requested'));
  }

  private _select(shop: IShopAccess) {
    window.dispatchEvent(
      new CustomEvent<IShopAccess>('shop-selected', { detail: shop })
    );
    router.navigate(resolveRouterPath(''));
  }

  private _goCreate() {
    router.navigate(resolveRouterPath('shop/create'));
  }

  private _goSample() {
    router.navigate(resolveRouterPath('shop/sample'));
  }

  // Caller can have at most 1 sample shop. Server enforces with a 400;
  // the button is hidden client-side once a sample exists to avoid the
  // noisy round-trip + error case. Read from the token snapshot on each
  // ShopAccess entry; falls back to false on older tokens (server still
  // 400s correctly).
  private _hasSampleShop(): boolean {
    return (this.userProfile?.shopAccess ?? []).some(
      (s) => s.active && s.isSample === true
    );
  }

  private _renderHeader() {
    const profile = this.userProfile;
    if (!profile) return nothing;
    const initials = (profile.displayName || profile.emailAddress || '?')
      .split(' ')
      .map((w: string) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
    return html`
      <div class="page-header">
        <div class="user-info">
          <sl-avatar initials=${initials} style="--size: 36px;"></sl-avatar>
          <div style="min-width: 0;">
            <div class="user-name">
              ${profile.displayName || profile.emailAddress}
            </div>
            ${
              profile.displayName && profile.emailAddress
                ? html`<div class="user-email">${profile.emailAddress}</div>`
                : nothing
            }
          </div>
        </div>
        <sl-button size="small" variant="default" @click=${this._signOut}>
          <sl-icon slot="prefix" name="box-arrow-right"></sl-icon>
          Sign out
        </sl-button>
      </div>
    `;
  }

  render() {
    const shops = (this.userProfile?.shopAccess ?? []).filter((s) => s.active);
    const samplePresent = this._hasSampleShop();

    if (shops.length === 0) {
      return html`
        ${this._renderHeader()}
        <sl-card class="empty">
          <h2>${write`${'ref:shop:none-title'}No shops yet`}</h2>
          <p>
            ${write`${'ref:shop:none-body'}Create your first shop to start setting up a menu, stock, and sales.`}
          </p>
          <div
            style="display: flex; flex-direction: column; gap: 10px; align-items: stretch;"
          >
            <sl-button variant="primary" @click=${this._goCreate}>
              <sl-icon slot="prefix" name="plus-circle"></sl-icon>
              ${write`${'ref:shop:create-first'}Create your first shop`}
            </sl-button>
            <sl-button variant="default" @click=${this._goSample}>
              <sl-icon slot="prefix" name="cup-hot"></sl-icon>
              ${write`${'ref:shop:try-sample'}Try with sample data`}
            </sl-button>
          </div>
        </sl-card>
      `;
    }

    return html`
      ${this._renderHeader()}
      <h2>${write`${'ref:shop:select-title'}Choose a shop`}</h2>
      <div class="shops">
        ${shops.map(
          (shop) => html`
            <sl-card class="shop-card" @click=${() => this._select(shop)}>
              <div class="name-row">
                <strong>${shop.shopDisplayName || shop.shopName}</strong>
                ${
                  shop.roles.includes('owner')
                    ? html`<sl-badge variant="primary"
                        >${write`${'ref:shop:role-owner'}Owner`}</sl-badge
                      >`
                    : nothing
                }
                ${
                  shop.isSample
                    ? html`<sl-badge variant="warning" pill>SAMPLE</sl-badge>`
                    : nothing
                }
              </div>
              <small>${shop.shopName}</small>
            </sl-card>
          `
        )}
      </div>
      <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px;">
        <sl-button variant="default" @click=${this._goCreate}>
          <sl-icon slot="prefix" name="plus-circle"></sl-icon>
          ${write`${'ref:shop:create-another'}Create another shop`}
        </sl-button>
        ${
          !samplePresent
            ? html`
                <sl-button variant="default" outline @click=${this._goSample}>
                  <sl-icon slot="prefix" name="cup-hot"></sl-icon>
                  ${write`${'ref:shop:try-sample'}Try with sample data`}
                </sl-button>
              `
            : nothing
        }
      </div>
    `;
  }
}
